import React, { createContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import Brightness4Icon from '@mui/icons-material/Brightness4'; // Moon icon
import Brightness7Icon from '@mui/icons-material/Brightness7'; // Sun icon
import Box from '@mui/material/Box';

// Create a context for theme mode
export const ColorModeContext = createContext({ 
  toggleColorMode: () => {},
  mode: 'dark' // Default to dark mode
});

export const ThemeProvider = ({ children }) => {
  // Always start with dark mode
  const [mode, setMode] = useState('dark');
  
  // Apply mode to html element for global CSS targeting
  useEffect(() => {
    document.documentElement.setAttribute('data-theme-mode', mode);
    // Force override any browser dark mode
    document.documentElement.style.colorScheme = mode;
  }, [mode]);
  
  // Custom theme color configurations
  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: {
        main: '#5869A7',
        light: '#7887b8',
        dark: '#3e4c85',
      },
      secondary: {
        main: '#f44336',
        light: '#f6685e',
        dark: '#aa2e25',
      },
      background: {
        default: mode === 'light' ? '#f5f5f5' : '#121212',
        paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
        appBar: mode === 'light' ? '#ffffff' : '#282828',
      },
      text: {
        primary: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
        secondary: mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            colorScheme: mode,
          },
          body: {
            backgroundColor: mode === 'light' ? '#f5f5f5' : '#121212',
            color: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
          },
          '.footer': {
            backgroundColor: mode === 'dark' ? '#000000' : '#F4F4F4',
            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'inherit',
          },
          '.form-control': {
            backgroundColor: mode === 'dark' ? '#383838' : '#fff',
            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'inherit',
            borderColor: mode === 'dark' ? '#555' : '#ced4da',
            '&:focus': {
              backgroundColor: mode === 'dark' ? '#3a3a3a' : '#fff',
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'inherit',
            }
          },
          '.card': {
            backgroundColor: mode === 'dark' ? '#282828' : '#fff',
            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'inherit',
          },
          '.list-group-item': {
            backgroundColor: mode === 'dark' ? '#333' : '#fff',
            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'inherit',
            borderColor: mode === 'dark' ? '#444' : 'rgba(0,0,0,.125)',
          }
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? '#ffffff' : '#282828',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? '#ffffff' : '#282828',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? '#ffffff' : '#282828',
            color: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiInputBase-input": {
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : undefined,
            },
            "& .MuiInputLabel-root": {
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : undefined,
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : undefined,
              },
              "&:hover fieldset": {
                borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : undefined,
              },
              "&.Mui-focused fieldset": {
                borderColor: mode === 'dark' ? '#90caf9' : undefined,
              },
            },
          },
        },
      },
    },
  }), [mode]);
  
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
      mode,
    }),
    [mode],
  );
  
  return (
    <ColorModeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ColorModeContext.Provider>
  );
};

// Move the toggle button to the bottom right
export const ThemeToggle = () => {
  const { mode, toggleColorMode } = React.useContext(ColorModeContext);
  
  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: '20px', // Changed from top to bottom
        right: '20px',
        zIndex: 1300,
        backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        borderRadius: '50%',
        padding: '5px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
      }}
    >
      <IconButton 
        onClick={toggleColorMode} 
        color="inherit"
        aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
      </IconButton>
    </Box>
  );
}; 